import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { apiService } from '@/services/api';
import ResearchesSidebar from '../Cluster/components/ResearchesSidebar';

const ResearchHome: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingSearches, setIsLoadingSearches] = useState(false);
  const [recentSearches, setRecentSearches] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter] = useState('');
  const [selectedSearchId, setSelectedSearchId] = useState<string | null>(null);

  const fetchRecentSearches = useCallback(async () => {
    setIsLoadingSearches(true);
    setError(null);
    try {
      const response = await apiService.get('/search/list/', {
        params: {
          page: 1,
          page_size: 50,
        },
      });
      console.log('Recent searches response:', response);
      setRecentSearches(response.data.results || []);
    } catch (error: any) {
      console.error('Error fetching recent searches:', error);
      // apiService throws ErrorResponse object, not axios error
      // setError(error.message || 'Failed to fetch recent searches');
      // Fallback to empty array
      setRecentSearches([]);
    } finally {
      setIsLoadingSearches(false);
    }
  }, []);

  // Fetch recent searches on component mount and when filters change
  useEffect(() => {
    fetchRecentSearches();
  }, [statusFilter, fetchRecentSearches]);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      setError('Please enter a research topic or keyword');
      return;
    }

    console.log('Searching for:', searchQuery);
    setError(null); // Clear any previous errors
    setIsSearching(true);
    
    try {
      const response = await apiService.post('/search/', {
        query: searchQuery
      }, {
        timeout: 300000 // 5 minutes
      });

      console.log('API response:', response.data);
      
      // Navigate to research details page with the search ID
      navigate(`/research/${response.data.search_record.id}`, {
        state: {
          searchQuery,
          results: response.data.search_record.keywords || []
        }
      });
    } catch (error: any) {
      console.error('Search error:', error);
      // apiService throws ErrorResponse object, not axios error
      setError(error.message || 'Search failed');
      // Navigate to research page with no results
      navigate('/research/new', {
        state: {
          searchQuery,
          results: []
        }
      });
    } finally {
      setIsSearching(false);
    }
  };


  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="flex gap-6">
      {/* Left Sidebar - Recent Researches */}
      <ResearchesSidebar
        searches={recentSearches}
        selectedSearchId={selectedSearchId}
        isLoadingSearches={isLoadingSearches}
        onSearchSelect={(searchId) => {
          setSelectedSearchId(searchId);
          navigate(`/research/${searchId}`);
        }}
        formatDate={formatDate}
      />

      {/* Main Content */}
      <div className="flex-1 space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-text-primary">Research Home</h1>
          <p className="text-muted-foreground">
            Comprehensive research tools and insights for data-driven decisions
          </p>
        </div>

      {/* Prominent Keyword Research Section */}
      <Card className="p-8">
        <CardContent className="p-0">
          <form onSubmit={handleSearch} className="space-y-6">
            <div className="space-y-4">
              <Input
                type="text"
                placeholder="Enter your research topic or keyword..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-16 text-lg border-2 border-secondary focus:border-primary focus:ring-primary"
                disabled={isSearching}
              />
            </div>
            <div className="flex flex-col items-center space-y-2">
              <Button 
                type="submit" 
                size="lg" 
                className="h-12 px-8 text-lg"
                disabled={isSearching}
              >
                {isSearching ? (
                  <div className="flex items-center space-x-2">
                    <div className="relative w-6 h-6">
                      {/* Orbit path */}
                      <div className="w-6 h-6 border-2 border-black/50 rounded-full"></div>
                      {/* Planet */}
                      <div 
                        className="absolute w-1.5 h-1.5 bg-black rounded-full" 
                        style={{
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          animation: 'orbit 1.5s linear infinite'
                        }}
                      ></div>
                    </div>
                    <style>{`
                      @keyframes orbit {
                        0% {
                          transform: translate(-50%, -50%) rotate(0deg) translateX(8px) rotate(0deg);
                        }
                        100% {
                          transform: translate(-50%, -50%) rotate(360deg) translateX(8px) rotate(-360deg);
                        }
                      }
                    `}</style>
                    <span>Analyzing...</span>
                  </div>
                ) : (
                  'Start Research'
                )}
              </Button>
              {error && <span className="text-red-600 text-sm">{error}</span>}
            </div>
          </form>
        </CardContent>
      </Card>

      </div>
    </div>
  );
};

export default ResearchHome; 